import {
  AppRuntimeConfig,
  ICON_NAMES
} from '../models/app-runtime-config.model';

import { DEFAULT_APP_CONFIG } from '../constants/default-app-config';

type UnknownRecord = Record<string, unknown>;

export function parseAppConfig(raw: unknown): AppRuntimeConfig {
  const root = readObject(raw, 'configuración');

  const application = readOptionalObject(root['application']);
  const layout = readOptionalObject(root['layout']);
  const features = readOptionalObject(root['features']);
  const theme = readOptionalObject(root['theme']);
  const colors = readOptionalObject(theme['colors']);
  const typography = readOptionalObject(theme['typography']);
  const radius = readOptionalObject(theme['radius']);
  const spacing = readOptionalObject(theme['spacing']);
  const assets = readOptionalObject(root['assets']);
  const logos = readOptionalObject(assets['logos']);
  const icons = readOptionalObject(assets['icons']);
  const entityIcons = readOptionalObject(assets['entityIcons']);

  const schemaVersion = readNumber(
    root,
    'schemaVersion',
    DEFAULT_APP_CONFIG.schemaVersion
  );

  if (schemaVersion !== 1) {
    throw new Error(
      `schemaVersion no soportado: ${schemaVersion}. Se esperaba 1.`
    );
  }

  const config: AppRuntimeConfig = {
    schemaVersion: 1,

    tenant: readString(
      root,
      'tenant',
      DEFAULT_APP_CONFIG.tenant
    ),

    application: {
      title: readString(
        application,
        'title',
        DEFAULT_APP_CONFIG.application.title
      ),

      language: readString(
        application,
        'language',
        DEFAULT_APP_CONFIG.application.language
      ),

      ariaLabel: readString(
        application,
        'ariaLabel',
        DEFAULT_APP_CONFIG.application.ariaLabel
      )
    },

    layout: {
      showNavigation: readBoolean(
        layout,
        'showNavigation',
        DEFAULT_APP_CONFIG.layout.showNavigation
      )
    },

    features: {
      showHeader: readBoolean(
        features,
        'showHeader',
        DEFAULT_APP_CONFIG.features.showHeader
      ),

      showFooter: readBoolean(
        features,
        'showFooter',
        DEFAULT_APP_CONFIG.features.showFooter
      ),

      showHelpButton: readBoolean(
        features,
        'showHelpButton',
        DEFAULT_APP_CONFIG.features.showHelpButton
      ),

      showPromotionalBanner: readBoolean(
        features,
        'showPromotionalBanner',
        DEFAULT_APP_CONFIG.features.showPromotionalBanner
      ),

      showDownloadButton: readBoolean(
        features,
        'showDownloadButton',
        DEFAULT_APP_CONFIG.features.showDownloadButton
      ),

      showContactSection: readBoolean(
        features,
        'showContactSection',
        DEFAULT_APP_CONFIG.features.showContactSection
      ),

      showMarketPlaceHeader: readBoolean(
        features,
        'showMarketPlaceHeader',
        DEFAULT_APP_CONFIG.features.showMarketPlaceHeader
      ),

      showCategoryFinder: readBoolean(
        features,
        'showCategoryFinder',
        DEFAULT_APP_CONFIG.features.showCategoryFinder
      )
    },

    theme: {
      colors: {
        primary: readColor(
          colors,
          'primary',
          DEFAULT_APP_CONFIG.theme.colors.primary
        ),

        onPrimary: readColor(
          colors,
          'onPrimary',
          DEFAULT_APP_CONFIG.theme.colors.onPrimary
        ),

        secondary: readColor(
          colors,
          'secondary',
          DEFAULT_APP_CONFIG.theme.colors.secondary
        ),

        onSecondary: readColor(
          colors,
          'onSecondary',
          DEFAULT_APP_CONFIG.theme.colors.onSecondary
        ),

        tertiary: readColor(
          colors,
          'tertiary',
          DEFAULT_APP_CONFIG.theme.colors.tertiary
        ),

        onTertiary: readColor(
          colors,
          'onTertiary',
          DEFAULT_APP_CONFIG.theme.colors.onTertiary
        ),

        background: readColor(
          colors,
          'background',
          DEFAULT_APP_CONFIG.theme.colors.background
        ),

        surface: readColor(
          colors,
          'surface',
          DEFAULT_APP_CONFIG.theme.colors.surface
        ),

        text: readColor(
          colors,
          'text',
          DEFAULT_APP_CONFIG.theme.colors.text
        ),

        mutedText: readColor(
          colors,
          'mutedText',
          DEFAULT_APP_CONFIG.theme.colors.mutedText
        ),

        border: readColor(
          colors,
          'border',
          DEFAULT_APP_CONFIG.theme.colors.border
        ),

        success: readColor(
          colors,
          'success',
          DEFAULT_APP_CONFIG.theme.colors.success
        ),

        warning: readColor(
          colors,
          'warning',
          DEFAULT_APP_CONFIG.theme.colors.warning
        ),

        labelBlue: readColor(
          colors,
          'labelBlue',
          DEFAULT_APP_CONFIG.theme.colors.labelBlue
        ),

        labelOrange: readColor(
          colors,
          'labelOrange',
          DEFAULT_APP_CONFIG.theme.colors.labelOrange
        ),

        marketPlaceBackground: readColor(
          colors,
          'marketPlaceBackground',
          DEFAULT_APP_CONFIG.theme.colors.marketPlaceBackground
        ),

        danger: readColor(
          colors,
          'danger',
          DEFAULT_APP_CONFIG.theme.colors.danger
        )
      },

      typography: {
        fontFamily: readFontFamily(
          typography,
          'fontFamily',
          DEFAULT_APP_CONFIG.theme.typography.fontFamily
        ),

        headingFontFamily: readFontFamily(
          typography,
          'headingFontFamily',
          DEFAULT_APP_CONFIG.theme.typography.headingFontFamily
        ),

        baseFontSize: readCssLength(
          typography,
          'baseFontSize',
          DEFAULT_APP_CONFIG.theme.typography.baseFontSize
        ),

        headingFontWeight: readFontWeight(
          typography,
          'headingFontWeight',
          DEFAULT_APP_CONFIG.theme.typography.headingFontWeight
        ),

        stylesheetUrl: readOptionalSafeUrl(
          typography,
          'stylesheetUrl'
        )
      },

      radius: {
        small: readCssLength(
          radius,
          'small',
          DEFAULT_APP_CONFIG.theme.radius.small
        ),

        medium: readCssLength(
          radius,
          'medium',
          DEFAULT_APP_CONFIG.theme.radius.medium
        ),

        large: readCssLength(
          radius,
          'large',
          DEFAULT_APP_CONFIG.theme.radius.large
        ),

        pill: readCssLength(
          radius,
          'pill',
          DEFAULT_APP_CONFIG.theme.radius.pill
        )
      },

      spacing: {
        small: readCssLength(
          spacing,
          'small',
          DEFAULT_APP_CONFIG.theme.spacing.small
        ),

        medium: readCssLength(
          spacing,
          'medium',
          DEFAULT_APP_CONFIG.theme.spacing.medium
        ),

        large: readCssLength(
          spacing,
          'large',
          DEFAULT_APP_CONFIG.theme.spacing.large
        )
      }
    },

    assets: {
      logos: {
        primary: readSafeUrl(
          logos,
          'primary',
          DEFAULT_APP_CONFIG.assets.logos.primary
        ),

        compact: readSafeUrl(
          logos,
          'compact',
          DEFAULT_APP_CONFIG.assets.logos.compact
        ),

        footer: readSafeUrl(
          logos,
          'footer',
          DEFAULT_APP_CONFIG.assets.logos.footer
        )
      },

      favicon: readSafeUrl(
        assets,
        'favicon',
        DEFAULT_APP_CONFIG.assets.favicon
      ),

      icons: {
        ...DEFAULT_APP_CONFIG.assets.icons,
        ...Object.fromEntries(
          ICON_NAMES.map(iconName => [
            iconName,
            readSafeUrl(
              icons,
              iconName,
              DEFAULT_APP_CONFIG.assets.icons[iconName]
            )
          ])
        )
      },

      entityIcons: readEntityIcons(entityIcons),

      marketPlaceBackgroundImage: readOptionalSafeUrl(
        assets,
        'marketPlaceBackgroundImage'
      )
    }
  };

  return config;
}

function readEntityIcons(source: UnknownRecord): Record<string, string> {
  const validLigature = /^[a-z0-9_]+$/;
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(source)) {
    if (key.startsWith('_comentario')) {
      continue;
    }

    if (typeof value !== 'string' || !validLigature.test(value.trim())) {
      throw new Error(
        `entityIcons.${key} debe ser un nombre de ícono válido (letras minúsculas, números y guion bajo).`
      );
    }

    result[key] = value.trim();
  }

  return result;
}

function readObject(
  value: unknown,
  propertyName: string
): UnknownRecord {
  if (!isObject(value)) {
    throw new Error(`${propertyName} debe ser un objeto.`);
  }

  return value;
}

function readOptionalObject(value: unknown): UnknownRecord {
  if (value === undefined || value === null) {
    return {};
  }

  if (!isObject(value)) {
    throw new Error('Se esperaba un objeto de configuración.');
  }

  return value;
}

function isObject(value: unknown): value is UnknownRecord {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value)
  );
}

function readString(
  source: UnknownRecord,
  property: string,
  fallback: string
): string {
  const value = source[property];

  if (value === undefined || value === null) {
    return fallback;
  }

  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${property} debe ser un texto no vacío.`);
  }

  return value.trim();
}

function readNumber(
  source: UnknownRecord,
  property: string,
  fallback: number
): number {
  const value = source[property];

  if (value === undefined || value === null) {
    return fallback;
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${property} debe ser numérico.`);
  }

  return value;
}

function readBoolean(
  source: UnknownRecord,
  property: string,
  fallback: boolean
): boolean {
  const value = source[property];

  if (value === undefined || value === null) {
    return fallback;
  }

  if (typeof value !== 'boolean') {
    throw new Error(`${property} debe ser booleano.`);
  }

  return value;
}

function readColor(
  source: UnknownRecord,
  property: string,
  fallback: string
): string {
  const value = readString(source, property, fallback);

  if (!/^#[0-9A-F]{6}$/i.test(value)) {
    throw new Error(
      `${property} debe usar formato hexadecimal de seis posiciones, por ejemplo #0057B8.`
    );
  }

  return value.toUpperCase();
}

function readCssLength(
  source: UnknownRecord,
  property: string,
  fallback: string
): string {
  const value = readString(source, property, fallback);

  const validLength =
    /^(0|[0-9]+(?:\.[0-9]+)?(?:px|rem|em|%|vh|vw))$/i;

  if (!validLength.test(value)) {
    throw new Error(
      `${property} contiene una medida CSS inválida.`
    );
  }

  return value;
}

function readFontWeight(
  source: UnknownRecord,
  property: string,
  fallback: number
): number {
  const value = readNumber(source, property, fallback);

  if (
    value < 100 ||
    value > 900 ||
    value % 100 !== 0
  ) {
    throw new Error(
      `${property} debe estar entre 100 y 900 en incrementos de 100.`
    );
  }

  return value;
}

function readFontFamily(
  source: UnknownRecord,
  property: string,
  fallback: string
): string {
  const value = readString(source, property, fallback);

  const validFontFamily = /^[a-zA-Z0-9\s'",\-]+$/;

  if (!validFontFamily.test(value)) {
    throw new Error(
      `${property} contiene caracteres no permitidos.`
    );
  }

  return value;
}

function readSafeUrl(
  source: UnknownRecord,
  property: string,
  fallback: string
): string {
  const value = readString(source, property, fallback);

  assertSafeUrl(value, property);

  return value;
}

function readOptionalSafeUrl(
  source: UnknownRecord,
  property: string
): string | undefined {
  const value = source[property];

  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  if (typeof value !== 'string') {
    throw new Error(`${property} debe ser una URL.`);
  }

  assertSafeUrl(value, property);

  return value;
}

function assertSafeUrl(
  value: string,
  property: string
): void {
  const normalizedValue = value.trim().toLowerCase();

  if (
    normalizedValue.startsWith('javascript:') ||
    normalizedValue.startsWith('data:') ||
    normalizedValue.startsWith('file:')
  ) {
    throw new Error(
      `${property} contiene un protocolo no permitido.`
    );
  }

  if (
    normalizedValue.startsWith('http://') &&
    !normalizedValue.startsWith('http://localhost') &&
    !normalizedValue.startsWith('http://127.0.0.1')
  ) {
    throw new Error(
      `${property} debe utilizar HTTPS fuera del entorno local.`
    );
  }
}
